/**
 * One player's whole history, in a side panel. Used by Live Game View and by
 * Game Results — one copy, both pages.
 *
 * Everything shown is read back from the server for the organization the page
 * is already on. The email is only ever filled in by the server when this user
 * is allowed to see player emails; the panel never guesses it from the row.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { Sheet, SheetContent } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { PlayerStatusPill, GamePill, TONES } from './GamePlayerChips';
import { PlayerBadgeActions } from './PlayerBadgeActions';
import { parseUtc } from '../../lib/datetime';
import { format } from 'date-fns';

const when = (value) => {
    if (!value) return '—';
    const d = parseUtc(value);
    return d ? format(d, 'MMM d, yyyy HH:mm') : String(value).slice(0, 16);
};

const Line = ({ label, value, testId }) => (
    <div className="flex justify-between gap-3 border-b border-border/60 py-1.5" data-testid={testId}>
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-sm text-right">{value ?? '—'}</span>
    </div>
);

export const PlayerHistoryPanel = ({ open, onOpenChange, organizationId, player, t, prefix,
                                     managerToken = null }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        if (!open || !player) { setData(null); setMissing(false); return; }
        let cancelled = false;
        setLoading(true);
        setMissing(false);
        api.get('/game-results/player', {
            ...(managerToken ? { headers: { 'X-Manager-Session': managerToken } } : {}),
            params: {
                organization_id: organizationId || undefined,
                player_key: player.player_key || player.row_id || undefined,
                fan_id: player.fan_id || undefined,
                email: player.email || undefined,
                player_name: player.player || undefined,
            },
        })
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch(() => { if (!cancelled) setMissing(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, player, organizationId, managerToken]);

    const teams = (game) => (game.home && game.away ? `${game.away} — ${game.home}` : game.label || '—');

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-full sm:max-w-lg overflow-y-auto"
                          data-testid={`${prefix}-player-panel`}>
                <div className="space-y-4 pt-6">
                    <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="text-lg font-bold" data-testid={`${prefix}-player-name`}>
                                {data?.screen_name || player?.player || '—'}
                            </p>
                            <PlayerStatusPill status={data?.status || player?.status} t={t}
                                              testId={`${prefix}-player-status`} />
                            {(data?.demo || player?.demo) && (
                                <Badge variant="outline" className="text-[10px]"
                                       data-testid={`${prefix}-player-demo`}>
                                    {t('gameBoard.demoTag', 'Demo')}
                                </Badge>
                            )}
                        </div>
                        {data?.email ? (
                            <p className="text-sm text-muted-foreground break-all"
                               data-testid={`${prefix}-player-email`}>{data.email}</p>
                        ) : data ? (
                            <p className="text-xs text-muted-foreground" data-testid={`${prefix}-player-email-hidden`}>
                                {t('gameBoard.emailHidden', 'Email not shown')}
                            </p>
                        ) : null}
                    </div>

                    {loading && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground"
                                         data-testid={`${prefix}-player-loading`} />}
                    {missing && (
                        <p className="text-sm text-muted-foreground" data-testid={`${prefix}-player-missing`}>
                            {t('gameBoard.noHistory', 'No history yet for this player.')}
                        </p>
                    )}

                    {/* Venue badges, tonight's venue and the phone — the same
                        actions as the Players box, where staff already are. */}
                    {!managerToken && (data?.email || player?.email) && (
                        <PlayerBadgeActions organizationId={organizationId}
                                            email={data?.email || player?.email}
                                            prefix={prefix} />
                    )}

                    {data && (
                        <>
                            <div>
                                <Line label={t('gameBoard.nights', 'Nights joined')} value={data.nights}
                                      testId={`${prefix}-player-nights`} />
                                <Line label={t('gameBoard.firstNight', 'First night')} value={data.first_night}
                                      testId={`${prefix}-player-first`} />
                                <Line label={t('gameBoard.lastNight', 'Latest night')} value={data.last_night}
                                      testId={`${prefix}-player-last`} />
                                <Line label={t('gameBoard.totalWins', 'Wins')} value={data.totals?.wins ?? 0}
                                      testId={`${prefix}-player-wins-total`} />
                                <Line label={t('gameBoard.totalPrizes', 'Prizes received')}
                                      value={`${data.totals?.vouchers ?? 0} · ${t('gameBoard.redeemedCount', '{{n}} used', { n: data.totals?.redeemed ?? 0 })}`}
                                      testId={`${prefix}-player-prizes-total`} />
                            </div>

                            <div className="space-y-2">
                                <p className="text-sm font-semibold">{t('gameBoard.gamesJoined', 'Games joined')}</p>
                                {(data.games || []).length === 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        {t('gameBoard.noGames', 'No game nights recorded yet.')}
                                    </p>
                                )}
                                {(data.games || []).map((game) => (
                                    <div key={game.night} className="rounded-md border p-3 space-y-1"
                                         data-testid={`${prefix}-player-game-${game.night}`}>
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <span className="text-sm font-medium">{game.night}</span>
                                            <span className="text-xs text-muted-foreground">{teams(game)}</span>
                                        </div>
                                        <p className="text-xs">
                                            {t('gameBoard.col.winnerPick', 'Winner pick')}:{' '}
                                            {game.winner_pick
                                                ? `${t(`gameBoard.pick.${game.winner_pick}`, game.winner_pick)}${game.winner_pick_paid ? ` · ${t('gameBoard.paid', 'paid')}` : ''}`
                                                : '—'}
                                        </p>
                                        <p className="text-xs">
                                            {t('gameBoard.col.starPick', 'Star pick')}: {game.star_pick || '—'}
                                        </p>
                                        <p className="text-xs">
                                            {t('gameBoard.col.result', 'Result')}:{' '}
                                            {game.scratches
                                                ? t(`gameBoard.result.${game.result === 'win' ? 'win' : 'lose'}`, game.result)
                                                : t('gameBoard.result.pending', 'Waiting')}
                                        </p>
                                        {game.prize && (
                                            <p className="text-xs">{t('gameBoard.col.prize', 'Prize')}: {game.prize}</p>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {(data.wins || []).length > 0 && (
                                <div className="space-y-2">
                                    <p className="text-sm font-semibold">{t('gameBoard.allWins', 'Every win')}</p>
                                    {data.wins.map((win, i) => (
                                        <div key={`${win.voucher || win.at}-${i}`}
                                             className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 py-1.5"
                                             data-testid={`${prefix}-player-win-${i}`}>
                                            <span className="text-xs">{win.night} · {win.prize || '—'}</span>
                                            {win.doubled && (
                                                <GamePill tone={TONES.violet} label={t('gameBoard.doubled', 'Doubled')} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="space-y-2">
                                <p className="text-sm font-semibold">{t('gameBoard.prizesGiven', 'Prizes received')}</p>
                                {(data.vouchers || []).length === 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        {t('gameBoard.noPrizes', 'No prizes yet.')}
                                    </p>
                                )}
                                {(data.vouchers || []).map((v, i) => (
                                    <div key={v.code || i} className="rounded-md border p-3 space-y-1"
                                         data-testid={`${prefix}-player-voucher-${v.code || i}`}>
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <span className="text-sm">{v.title || v.code}</span>
                                            <GamePill tone={v.redeemed ? TONES.green : TONES.blue}
                                                      label={v.redeemed
                                                          ? t('gameBoard.voucher.redeemed', 'Used')
                                                          : t(`gameBoard.voucher.${v.status || 'unused'}`, v.status || 'unused')} />
                                        </div>
                                        <p className="text-xs font-mono text-muted-foreground">{v.code}</p>
                                        <p className="text-xs">{t('gameBoard.given', 'Given')}: {when(v.issued_at)}</p>
                                        {v.redeemed && (
                                            <p className="text-xs" data-testid={`${prefix}-player-voucher-used-${v.code || i}`}>
                                                {t('gameBoard.usedOn', 'Used')}: {when(v.redeemed_at)}
                                                {v.redeemed_by ? ` · ${v.redeemed_by}` : ''}
                                            </p>
                                        )}
                                        {v.source && (
                                            <p className="text-xs text-muted-foreground">
                                                {t('gameBoard.source', 'Source')}: {t(`gameBoard.sourceName.${v.source}`, v.source)}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};
